// Conversacion de un caso o de un correo (D40), igual para el correo del buzon y para el historial de Zammad: una
// columna que se lee facil (el cliente a la izquierda, nosotros a la derecha, notas internas en amarillo), solo el
// mensaje (sin lo citado ni la firma; "Ver mensaje completo" lo muestra todo) y, en cada mensaje, Responder,
// Responder a todos y Reenviar, como en Zammad.
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { type AdjuntoRecibido, adjuntosDe, archivoAdjunto, textoCompletoDe } from '../api/correo';
import { soloElMensaje, type ModoRespuesta } from '../lib/correo';
import { ago } from '../lib/format';
import { Icon } from '../lib/icons';
import { AdjuntosCorreo } from './AdjuntosCorreo';
import { EnlaceCaso } from './EnlaceCaso';
import { type ImagenesDe, TextoConImagenes } from './TextoConImagenes';
import { Avatar } from './bits';

export type MsgConv = {
  id: string;
  asunto: string;
  de: string;
  deNombre: string;
  para: string[];
  cc: string[];
  fecha: string;
  texto: string;
  mio: boolean;
  interno?: boolean;
  // Id del correo en el motor, para traer sus adjuntos (solo el correo del buzon).
  adjuntosDe?: string | null;
  headerMessageId?: string | null;
  zammadId?: number | null;
  // Copia al caso de un correo de un buzon personal (capa 3): su Message-ID de origen, para no repetirlo.
  origen?: string | null;
  copiado?: boolean;
  // Correo de un buzon que quien mira no puede leer (capa 4): solo una linea con quien y cuando.
  oculto?: boolean;
};

// "22-oct 4:34 p. m." (la fecha exacta va en el title).
export const fechaCorta = (f: string) => {
  const d = new Date(f);
  const mes = d.toLocaleDateString('es-CO', { month: 'short' }).replace(/\.$/, '');
  return `${d.getDate()}-${mes} ${d.toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit' })}`;
};

// Lo que el paso de HTML a texto dejo escapado ("&nbsp;", "&amp;"...), para que no se vea suelto (D42).
const ENT: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
export const sinEntidades = (t: string) => t.replace(/&(nbsp|amp|lt|gt|quot|apos);/gi, (m, e: string) => ENT[e.toLowerCase()] ?? m).replace(/[ \t]+\n/g, '\n');

// Correos, enlaces y numeros de caso ("#20260218620051", p. ej. en "Unido al caso #...") del texto se pueden abrir
// (como en Zammad). Solo se arman enlaces; el resto queda como texto.
const ENLACE = /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}|#\d{10,20}\b)/gi;
export function ConEnlaces({ texto }: { texto: string }) {
  const partes: ReactNode[] = [];
  let desde = 0;
  for (const m of texto.matchAll(ENLACE)) {
    let t = m[0];
    const sobra = /[.,;:)\]]+$/.exec(t)?.[0] ?? '';
    t = t.slice(0, t.length - sobra.length);
    const i = m.index ?? 0;
    partes.push(texto.slice(desde, i));
    if (/^#\d+$/.test(t)) {
      partes.push(<EnlaceCaso key={i} numero={t.slice(1)} />);
      desde = i + t.length;
      continue;
    }
    const href = t.includes('@') && !/^https?:/i.test(t) ? `mailto:${t}` : /^https?:/i.test(t) ? t : `https://${t}`;
    partes.push(
      <a key={i} href={href} target="_blank" rel="noreferrer noopener">
        {t}
      </a>,
    );
    desde = i + t.length;
  }
  partes.push(texto.slice(desde));
  return <>{partes}</>;
}

export function Conversacion({
  mensajes,
  mios,
  onAccion,
  elegido,
  adjuntos,
  imagenes,
}: {
  mensajes: MsgConv[];
  mios: string[];
  onAccion: (m: MsgConv, modo: ModoRespuesta) => void;
  elegido?: string | null;
  // Adjuntos de un mensaje que no viene del buzon (Zammad): `visible` es el texto que se muestra.
  adjuntos?: (m: MsgConv, visible: string, todo: string) => ReactNode;
  // Imagenes de un mensaje que no es del buzon (Zammad), para ponerlas dentro del texto.
  imagenes?: (m: MsgConv) => ImagenesDe | null;
}) {
  const [completos, setCompletos] = useState<Set<string>>(new Set());
  // Al abrir (o al llegar un mensaje nuevo) se ve lo ultimo, no lo primero (D42).
  const fin = useRef<HTMLDivElement | null>(null);
  const cuantos = useRef(0);
  useEffect(() => {
    if (mensajes.length === cuantos.current) return;
    cuantos.current = mensajes.length;
    const caja = fin.current?.closest('.ch-ms');
    if (caja) caja.scrollTop = caja.scrollHeight;
  }, [mensajes.length]);
  const propios = new Set(mios.map((x) => x.toLowerCase()));
  return (
    <div className="conv">
      {mensajes.map((m) => {
        if (m.oculto)
          return (
            <div key={m.id} className="conv-oculto" role="note">
              <Icon name="lock" size={12} />
              <span>
                <b>{m.deNombre}</b> respondió desde su buzón · <time title={new Date(m.fecha).toLocaleString('es-CO')}>{fechaCorta(m.fecha)}</time>
              </span>
            </div>
          );
        const todo = sinEntidades(m.texto);
        const limpio = soloElMensaje(todo);
        const completo = completos.has(m.id) || limpio === todo.trim();
        const otros = [...m.para, ...m.cc].filter((x) => !propios.has(x.toLowerCase()) && x.toLowerCase() !== m.de.toLowerCase());
        return (
          <article key={m.id} className={`conv-m ${m.mio ? 'mio' : ''} ${m.interno ? 'interno' : ''} ${elegido === m.id ? 'elegido' : ''}`}>
            <header className="conv-h">
              <Avatar nombre={m.deNombre || m.de || '?'} size="sm" />
              <div className="conv-quien">
                <b>{m.interno ? `Nota interna · ${m.deNombre || 'Equipo'}` : m.deNombre || m.de || (m.mio ? 'Equipo' : 'Cliente')}</b>
                {m.de && m.deNombre && m.de !== m.deNombre ? <span className="sb">{m.de}</span> : null}
                {m.copiado ? (
                  <span className="conv-copia" title="Se escribió desde un buzón personal y se copió al caso para que lo vea todo el grupo">
                    Copiado de su buzón
                  </span>
                ) : null}
                {!m.interno && (m.para.length || m.cc.length) ? (
                  <span className="conv-para">
                    {/* El motor a veces guarda dos veces a quien se envio: cada direccion una sola vez. */}
                    {m.para.length ? `Para: ${[...new Set(m.para)].join(', ')}` : ''}
                    {m.cc.length ? `${m.para.length ? ' · ' : ''}CC: ${[...new Set(m.cc)].join(', ')}` : ''}
                  </span>
                ) : null}
              </div>
              <time title={new Date(m.fecha).toLocaleString('es-CO')}>{ago(m.fecha)}</time>
            </header>
            {m.adjuntosDe ? null : (
              <>
                <div className="conv-t">
                  <TextoConImagenes texto={completo ? todo.trim() : limpio || '(sin texto)'} imagenes={imagenes?.(m)} />
                </div>
                {!completo || completos.has(m.id) ? (
                  <button className="linkish conv-mas" onClick={() => setCompletos((s) => (s.has(m.id) ? new Set([...s].filter((x) => x !== m.id)) : new Set(s).add(m.id)))}>
                    {completos.has(m.id) ? 'Ver solo el mensaje' : 'Ver mensaje completo (firma y citas)'}
                  </button>
                ) : null}
              </>
            )}
            {m.adjuntosDe ? <TextoDelBuzon m={m} limpio={limpio} todo={todo} visible={completo ? todo : limpio} /> : null}
            {!m.adjuntosDe && adjuntos ? adjuntos(m, completo ? todo : limpio, todo) : null}
            {m.interno ? null : (
              <footer className="conv-acc">
                <button type="button" onClick={() => onAccion(m, 'responder')}>
                  <Icon name="reply" size={13} /> Responder
                </button>
                {otros.length ? (
                  <button type="button" onClick={() => onAccion(m, 'todos')}>
                    <Icon name="users" size={13} /> Responder a todos
                  </button>
                ) : null}
                <button type="button" onClick={() => onAccion(m, 'reenviar')}>
                  <Icon name="send" size={13} /> Reenviar
                </button>
              </footer>
            )}
          </article>
        );
      })}
      <div ref={fin} />
    </div>
  );
}

// Texto de un correo del buzon (D43). El motor guarda solo la parte nueva: lo citado y lo reenviado se traen del buzon
// al pedir "Ver el correo completo". En un reenvio (FW/RV) se trae solo, porque lo importante suele venir abajo.
const esReenvio = (asunto: string) => /^\s*(fw|fwd|rv|reenv[ií]o)\s*:/i.test(asunto);
// Debajo, los adjuntos: las imagenes que ya salen dentro del texto que se ve no se repiten.
function TextoDelBuzon({ m, limpio, todo, visible }: { m: MsgConv; limpio: string; todo: string; visible: string }) {
  const [ver, setVer] = useState(() => esReenvio(m.asunto));
  const r = useQuery({ queryKey: ['correo-completo', m.adjuntosDe], queryFn: () => textoCompletoDe(m.adjuntosDe!), enabled: ver, staleTime: 30 * 60_000, retry: 1 });
  const completo = ver && r.data ? sinEntidades(r.data).trim() : null;
  // Misma consulta que AdjuntosCorreo (se comparte la cache): las imagenes pegadas van dentro del texto.
  const adj = useQuery({ queryKey: ['adjuntos', m.adjuntosDe], queryFn: () => adjuntosDe(m.adjuntosDe!), staleTime: 30 * 60_000, retry: 1 });
  const imagenes = adj.data?.length ? { lista: adj.data, traer: (a: AdjuntoRecibido) => archivoAdjunto(m.adjuntosDe!, a.parte), clave: m.adjuntosDe! } : null;
  return (
    <>
      <div className="conv-t">
        <TextoConImagenes texto={completo ?? (limpio || '(sin texto)')} imagenes={imagenes} />
      </div>
      {ver && r.isLoading ? <span className="note">Trayendo el correo completo del buzón…</span> : null}
      {ver && r.error ? <span className="note">No se pudo traer el correo completo: {(r.error as Error).message}</span> : null}
      <button className="linkish conv-mas" onClick={() => setVer(!ver)}>
        {ver ? 'Ver solo el mensaje' : 'Ver el correo completo (con lo citado y reenviado)'}
      </button>
      <AdjuntosCorreo mensajeId={m.adjuntosDe!} texto={completo ?? todo} visible={completo ?? visible} />
    </>
  );
}

// Notas internas del caso (las que se escriben en el CRM) como mensajes de la conversacion, en su lugar por fecha.
export const notasComoMensajes = (notas: { autor: string; fecha: string; texto: string }[] = []): MsgConv[] =>
  notas.map((n, i) => ({ id: `nota-${i}-${n.fecha}`, asunto: '', de: '', deNombre: n.autor, para: [], cc: [], fecha: n.fecha, texto: n.texto, mio: true, interno: true }));
export const juntarPorFecha = (a: MsgConv[], b: MsgConv[]) => [...a, ...b].sort((x, y) => Date.parse(x.fecha) - Date.parse(y.fecha));

// "Ana Perez <ana@x.co>, pedro@y.co" -> correos
export const correosDe = (s: string) => [...s.matchAll(/[^\s<>,;"()]+@[^\s<>,;"()]+/g)].map((x) => x[0].toLowerCase());
// "Ana Perez <ana@x.co>" -> "Ana Perez"; "ana@x.co" -> "ana@x.co"
export const nombreDeDireccion = (s: string) => (/^\s*"?([^"<]+?)"?\s*</.exec(s)?.[1] ?? s).trim();
